import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { JSX } from 'react';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import ListSubheader from '@mui/material/ListSubheader';
import { useMediaQuery, useTheme } from '@mui/material';

import { useNavigationContext } from '../../contexts/NavigationContext/hooks/useNavigationContext.hook';

import { DRAWER_WIDTH } from '../constants';

import { useMenu } from './hooks/useMenu.hook';
import { useScrollToCurrentSpace } from './hooks/useScrollToCurrentSpace.hook';
import { SPACE_LIST_ID } from './constants/space-list-id.constant';
import { Space } from './enums/space.enum';

interface Props {
  readonly onDrawerClose: () => void;
  readonly mobileOpen: boolean;
}

export const AppMenu = ({
  onDrawerClose: handleDrawerClose,
  mobileOpen,
}: Readonly<Props>): JSX.Element => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('sm'));

  const {
    navigation: { page: currentPage },
  } = useNavigationContext();

  const { menu, currentSpace, currentMenuSpaceInfo } = useMenu(currentPage);

  const { containerRef, toolbarRef } = useScrollToCurrentSpace(
    currentSpace,
    !!currentSpace && !menu[currentSpace].hidden,
  );

  const getPages = useCallback(
    (space: Space) => {
      const menuSpaceInfo = menu[space];

      return menuSpaceInfo.pages.map(
        ({ disabled, Icon, id, secondaryAction, title, to }) => (
          <ListItem
            disablePadding
            id={`app-menu-${space}-space-${id}-page`}
            key={id}
            onClick={handleDrawerClose}
            secondaryAction={secondaryAction}
          >
            <ListItemButton
              component={Link}
              disabled={
                (currentSpace !== space &&
                  currentMenuSpaceInfo &&
                  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                  !currentMenuSpaceInfo.enabledSiblings.includes(space)) ||
                menuSpaceInfo.disabled ||
                disabled
              }
              id={`app-menu-${space}-space-${id}-page-link`}
              selected={currentPage === id}
              to={to}
            >
              <ListItemIcon>
                <Icon />
              </ListItemIcon>
              <ListItemText primary={title} />
            </ListItemButton>
          </ListItem>
        ),
      );
    },
    [currentSpace, currentMenuSpaceInfo, currentPage, handleDrawerClose, menu],
  );

  const drawer = useMemo(
    () => (
      <>
        <Toolbar ref={toolbarRef} />
        <List
          aria-labelledby="Liens menant vers les principales fonctionnalités de e-SOCNA"
          component="nav"
          id={SPACE_LIST_ID[Space.Main]}
        >
          {getPages(Space.Main)}
        </List>
        <Divider />
        <List
          aria-labelledby="Liens menant vers les fonctionnalités liées aux dossiers"
          component="nav"
          id={SPACE_LIST_ID[Space.Folder]}
          subheader={
            <ListSubheader component="div" id="app-menu-folder-space-subheader">
              Espace Dossier
            </ListSubheader>
          }
        >
          {getPages(Space.Folder)}
        </List>
        <Divider />
        <List
          aria-labelledby="Liens menant vers les fonctionnalités liées aux missions"
          component="nav"
          id={SPACE_LIST_ID[Space.Mission]}
          subheader={
            <ListSubheader
              component="div"
              id="app-menu-mission-space-subheader"
            >
              {menu[Space.Mission].title}
            </ListSubheader>
          }
        >
          {getPages(Space.Mission)}
        </List>
        <Divider />
        {/* Only within a collaboration, its sub-missions get their own space. */}
        {!menu[Space.SubMission].hidden && (
          <>
            <List
              aria-labelledby="Liens menant vers les fonctionnalités liées aux sous-missions"
              component="nav"
              id={SPACE_LIST_ID[Space.SubMission]}
              subheader={
                <ListSubheader
                  component="div"
                  id="app-menu-sub-mission-space-subheader"
                >
                  {menu[Space.SubMission].title}
                </ListSubheader>
              }
            >
              {getPages(Space.SubMission)}
            </List>
            <Divider />
          </>
        )}
        <List
          aria-labelledby="Liens menant vers les master data de e-SOCNA"
          component="nav"
          id={SPACE_LIST_ID[Space.MasterData]}
          subheader={
            <ListSubheader
              component="div"
              id="app-menu-master-data-space-subheader"
            >
              Master Data
            </ListSubheader>
          }
        >
          {getPages(Space.MasterData)}
        </List>
        <Divider />
        <List
          aria-labelledby="Liens menant vers le référentiel de e-SOCNA"
          component="nav"
          id={SPACE_LIST_ID[Space.References]}
          subheader={
            <ListSubheader
              component="div"
              id="app-menu-references-space-subheader"
            >
              Référentiel
            </ListSubheader>
          }
        >
          {getPages(Space.References)}
        </List>
        {!menu[Space.Admin].hidden && (
          <>
            <Divider />
            <List
              aria-labelledby="Liens menant vers les pages d'administration de e-SOCNA"
              component="nav"
              id={SPACE_LIST_ID[Space.Admin]}
              subheader={
                <ListSubheader
                  component="div"
                  id="app-menu-admin-space-subheader"
                >
                  Administration
                </ListSubheader>
              }
            >
              {getPages(Space.Admin)}
            </List>
          </>
        )}
      </>
    ),
    [getPages, menu, toolbarRef],
  );

  return (
    <Box
      aria-label="Menu de l'application e-SOCNA"
      component="nav"
      sx={{
        width: { sm: DRAWER_WIDTH },
        flexShrink: { sm: 0 },
      }}
    >
      {matches ? (
        <Drawer
          open
          slotProps={{ paper: { ref: containerRef } }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
          variant="permanent"
        >
          {drawer}
        </Drawer>
      ) : (
        <Drawer
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          onClose={handleDrawerClose}
          open={mobileOpen}
          slotProps={{ paper: { ref: containerRef } }}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
          variant="temporary"
        >
          {drawer}
        </Drawer>
      )}
    </Box>
  );
};
